/* pk-product — PDP behaviour (values from the design prototype):
   frame/size selection mapped to real variants (framed has no Masterpiece),
   live price + mockup frame + gallery scale [0.73,.80,.88,.94,1.0] 340ms,
   wall-view true-to-life width (long/short inches ÷ 132in scene), qty 1–9,
   view tabs, sticky buy-bar via IntersectionObserver, info modals. */
class PKProduct extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-pk-variants]').textContent);
    this.land = this.dataset.land === 'true';
    this.qty = 1;

    // on-white scale is computed per frame mode: the largest size AVAILABLE under the
    // current frame fills the preview (framed → Monumental 40in, unframed → Masterpiece 45in),
    // and every smaller size renders at its true physical ratio to that maximum.
    // Wall/scale inches are parsed from the option value's inch dims ("… - 40x60cm/16x24\""),
    // so size NAMES can change freely without touching this file.
    // visible wall width of the interior scene: the 150cm sideboard spans
    // ~45.4% of frame width → 150 / 0.454 ≈ 330cm ≈ 130in
    this.WALL_INCHES = 130;

    const onSwatch = this.querySelector('.pk-swatch--on');
    const onSize = this.querySelector('.pk-sizeopt--on');
    this.frame = onSwatch?.dataset.pkFrame || this.variants[0].frame;
    this.frameKey = onSwatch?.dataset.pkFrameKey || 'black';
    // trust the server-selected default (Black/Statement, or the ?variant= deep link)
    this.size = onSize?.dataset.pkSize || this.variants[0].size;

    this.addEventListener('click', (e) => this.onClick(e));
    this.setupBuyBar();
    this.setupBuyNow();
    this.setupGallery();
    this.sync();
  }

  onClick(e) {
    const swatch = e.target.closest('[data-pk-frame]');
    if (swatch) {
      this.frame = swatch.dataset.pkFrame;
      this.frameKey = swatch.dataset.pkFrameKey;
      this.sync();
      return;
    }
    const sizeBtn = e.target.closest('[data-pk-size]');
    if (sizeBtn && !sizeBtn.classList.contains('pk-sizerow--unavailable')) {
      this.size = sizeBtn.dataset.pkSize;
      this.sync();
      return;
    }
    const qtyBtn = e.target.closest('[data-pk-pdp-qty]');
    if (qtyBtn) {
      this.qty = Math.min(9, Math.max(1, this.qty + (qtyBtn.dataset.pkPdpQty === 'inc' ? 1 : -1)));
      this.sync();
      return;
    }
    const tab = e.target.closest('[data-pk-view]');
    if (tab) {
      this.goToSlideKey(tab.dataset.pkView);
      return;
    }
    const modalOpen = e.target.closest('[data-pk-modal-open]');
    if (modalOpen) {
      this.querySelector(`[data-pk-modal="${modalOpen.dataset.pkModalOpen}"]`).hidden = false;
      document.body.classList.add('overflow-hidden');
      return;
    }
    if (e.target.closest('[data-pk-modal-close]')) {
      this.closeModals();
      return;
    }
    if (e.target.closest('[data-pk-buybar-add]')) {
      this.addToCart();
    }
  }

  closeModals() {
    this.querySelectorAll('[data-pk-modal]').forEach((m) => (m.hidden = true));
    document.body.classList.remove('overflow-hidden');
  }

  currentVariant() {
    return this.variants.find((v) => v.frame === this.frame && v.size === this.size);
  }

  // Server-formatted USD string for a variant. Display-only local-currency
  // estimates are layered on top by pk-currency.js — see priceInto().
  fmt(v) {
    return v ? (v.price_money || '$' + Math.round(v.price / 100).toLocaleString('en-US')) : '';
  }

  // Render a variant's price into an element, tagged for pk-currency.js so the
  // estimate survives both variant switches and later currency changes.
  priceInto(el, v, mode) {
    if (!v) { el.textContent = ''; return; }
    if (window.pkCurrency) el.innerHTML = window.pkCurrency.tag(v.price, mode, this.fmt(v));
    else el.textContent = this.fmt(v);
  }

  sizeMeta() {
    // inch dims live in the option value itself: "Statement - 40x60cm/16x24\""
    const inch = (this.size.split('/')[1] || '').replace('"', '');
    const parts = inch.split('x').map((n) => parseFloat(n));
    if (parts.length === 2 && parts.every((n) => n > 0)) {
      return { longIn: Math.max(parts[0], parts[1]), shortIn: Math.min(parts[0], parts[1]) };
    }
    return { longIn: 24, shortIn: 16 };
  }

  sync() {
    const variant = this.currentVariant();

    // swatches
    this.querySelectorAll('[data-pk-frame]').forEach((b) => {
      const on = b.dataset.pkFrame === this.frame;
      b.classList.toggle('pk-swatch--on', on);
      b.setAttribute('aria-checked', on);
    });

    // size rows: availability under current frame + per-row price
    this.querySelectorAll('[data-pk-size]').forEach((b) => {
      const v = this.variants.find((x) => x.frame === this.frame && x.size === b.dataset.pkSize);
      b.classList.toggle('pk-sizerow--unavailable', !v);
      const priceEl = b.querySelector('[data-pk-size-price]');
      if (priceEl) {
        if (v) this.priceInto(priceEl, v, 'compact');
        else priceEl.textContent = '—';
      }
      const on = b.dataset.pkSize === this.size && !!v;
      b.classList.toggle('pk-sizeopt--on', on);
      b.setAttribute('aria-checked', on);
    });

    // selected size may not exist under this frame (framed × Masterpiece) → snap to Statement
    if (!variant) {
      const fallback = this.variants.find((x) => x.frame === this.frame && (x.size.startsWith('Statement') || x.size.startsWith('Large'))) ||
        this.variants.find((x) => x.frame === this.frame);
      if (fallback) {
        this.size = fallback.size;
        return this.sync();
      }
    }

    const sizeName = this.size.split(' - ')[0].trim();
    const frameLabel = this.frame === 'Unframed' ? 'Unframed' : this.frame + ' Frame';
    const label = `${frameLabel} · ${sizeName} Size`;
    this.querySelectorAll('[data-pk-variant-label]').forEach((el) => (el.textContent = label));
    this.querySelectorAll('[data-pk-price]').forEach((el) => this.priceInto(el, variant, el.dataset.pkPriceMode || 'compact'));

    // caption
    const cap = this.querySelector('[data-pk-frame-caption]');
    if (cap) {
      cap.textContent = this.frame === 'Unframed' ? 'Printed unframed' : `Frames include white mat · ${this.frame} frame`;
    }

    // mockup frame finish (gallery + wall + buybar thumbs)
    this.querySelectorAll('.pk-fp').forEach((fp) => {
      fp.className = fp.className.replace(/pk-fp--(black|white|natural|none)\b/, `pk-fp--${this.frameKey}`);
    });

    // gallery scale — true ratio to the largest size available under this frame
    const meta = this.sizeMeta();
    const maxLong = this.frame === 'Unframed' ? 45 : 40;
    const scaleEl = this.querySelector('[data-pk-gallery-scale]');
    if (scaleEl) scaleEl.style.transform = `scale(${(meta.longIn / maxLong).toFixed(3)})`;

    // wall width: print long side ÷ 132in scene width
    const wallEl = this.querySelector('[data-pk-wall-print]');
    if (wallEl) {
      const inches = this.land ? meta.longIn : meta.shortIn;
      wallEl.style.width = ((inches / this.WALL_INCHES) * 100).toFixed(1) + '%';
    }

    // dims caption (design: "40x60cm · 16x24\"")
    const dims = (this.size.split(' - ')[1] || '').replace('/', ' · ');
    const dimsEl = this.querySelector('[data-pk-dims-label]');
    if (dimsEl) dimsEl.textContent = dims;

    // qty + form inputs
    const qtyEl = this.querySelector('[data-pk-pdp-qty-value]');
    if (qtyEl) qtyEl.textContent = this.qty;
    const idInput = this.querySelector('[data-pk-variant-id]');
    if (idInput && variant) idInput.value = variant.id;
    const qtyInput = this.querySelector('[data-pk-qty-input]');
    if (qtyInput) qtyInput.value = this.qty;

    const atc = this.querySelector('[data-pk-atc]');
    if (atc) atc.disabled = !variant || !variant.available;
  }

  /* ── horizontal view gallery ── */
  setupGallery() {
    this.track = this.querySelector('[data-pk-viewer-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-pk-slide]')];
    this.prevArrow = this.querySelector('[data-pk-viewer-prev]');
    this.nextArrow = this.querySelector('[data-pk-viewer-next]');
    this.prevArrow?.addEventListener('click', () => this.scrollGallery(-1));
    this.nextArrow?.addEventListener('click', () => this.scrollGallery(1));
    this.track.addEventListener('scroll', () => this.syncGallery(), { passive: true });
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
    window.addEventListener('resize', () => this.syncGallery());
    this.syncGallery();
  }

  scrollGallery(dir) {
    this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
  }

  goToSlideKey(key) {
    if (!this.track) return;
    const idx = this.slides.findIndex((s) => s.dataset.pkSlide === key);
    if (idx >= 0) this.track.scrollTo({ left: idx * this.track.clientWidth, behavior: 'smooth' });
  }

  syncGallery() {
    if (!this.track) return;
    const w = this.track.clientWidth || 1;
    const idx = Math.round(this.track.scrollLeft / w);
    const key = this.slides[idx]?.dataset.pkSlide;
    // active tab reflects the visible slide
    this.querySelectorAll('[data-pk-view]').forEach((t) => {
      const on = t.dataset.pkView === key;
      t.classList.toggle('pk-viewtab--on', on);
      t.setAttribute('aria-selected', on);
    });
    // caption follows the visible slide
    this.querySelectorAll('[data-pk-cap]').forEach((c) => {
      c.hidden = c.dataset.pkCap !== key;
    });
    // adaptive arrows — hide at each end
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.prevArrow) this.prevArrow.hidden = this.track.scrollLeft <= 2;
    if (this.nextArrow) this.nextArrow.hidden = this.track.scrollLeft >= maxScroll - 2;
  }

  setBusy(selector, busy) {
    this.querySelectorAll(selector).forEach((b) => {
      b.classList.toggle('pk-btn--loading', busy);
      if (busy) b.setAttribute('aria-busy', 'true');
      else b.removeAttribute('aria-busy');
      b.disabled = busy;
    });
  }

  async addToCart() {
    const variant = this.currentVariant();
    if (!variant || !window.PKCart) return;
    const fd = new FormData();
    fd.append('id', variant.id);
    fd.append('quantity', this.qty);
    this.setBusy('[data-pk-atc]', true);
    try {
      await window.PKCart.add(fd);
    } catch (err) {
      console.error(err);
    } finally {
      this.setBusy('[data-pk-atc]', false);
    }
  }

  setupBuyNow() {
    const form = this.querySelector('[data-pk-product-form]');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.addToCart();
    });
    this.querySelector('[data-pk-buynow]')?.addEventListener('click', async () => {
      const variant = this.currentVariant();
      if (!variant) return;
      this.setBusy('[data-pk-buynow]', true);
      try {
        const res = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: this.qty }),
        });
        // keep the spinner running through the redirect; only reset on failure
        if (res.ok) window.location.href = '/checkout';
        else this.setBusy('[data-pk-buynow]', false);
      } catch (err) {
        console.error(err);
        this.setBusy('[data-pk-buynow]', false);
      }
    });
  }

  setupBuyBar() {
    const bar = this.querySelector('[data-pk-buybar]');
    const anchor = this.querySelector('#pk-atc');
    if (!bar || !anchor) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', !show);
        // keep the hidden buy-bar out of the tab order + a11y tree (its buttons
        // stay focusable under aria-hidden otherwise → phantom tab stops)
        bar.toggleAttribute('inert', !show);
      },
      { threshold: 0 }
    );
    io.observe(anchor);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeModals();
    });
  }
}
customElements.define('pk-product', PKProduct);
